/**
 * AdminSchedulePanel - 4-step admin schedule per active pathway, with an
 * optional Longevity Profile toggle (cardiometabolic + longevity
 * expansion 2026-05-07).
 *
 * Anchored to the prototype's "Step-by-Step Schedule Panel" surface
 * (https://ai-native-prototype.vercel.app/care-pathway-studio.html PROMs
 * tab). The prototype renders four steps with status badges + instrument
 * chips; this build mirrors that structure and resolves the schedule
 * dynamically from the active patient's pathway field.
 *
 * Pathway resolution:
 *   - Read the active patient via `usePatientStore.activePatientId`.
 *   - Resolve the patient fixture and read `patient.pathway`.
 *   - Look up the schedule in `PROM_SCHEDULES_BY_PATHWAY`. If the active
 *     patient is missing or has no recognized pathway, fall back to the
 *     Cardiometabolic Pathway schedule (the canonical Phase 1 path).
 *
 * Longevity Profile:
 *   - Rendered as a profile, not a pathway. The toggle is a single
 *     button on the schedule header. When enabled, the Longevity
 *     Profile schedule is appended below the active pathway schedule.
 *
 * Brand-Patch v2: every user-facing string with a pathway reference
 * uses the clinical pathway name from `pathwayDisplay`; no TLP1-4
 * literal anywhere.
 *
 * HC2: tokens-only.
 * HC5: zero em / en dashes.
 * HC6 a11y: schedule rows are `role="listitem"` inside `role="list"`;
 *   the toggle is a real `<button>` with `aria-pressed`.
 */
import { useState } from 'react';
import { usePatientStore } from '../../state/patient.store';
import { getPatientById } from '../../data/synthetic-patients';
import {
  LONGEVITY_PROFILE_SCHEDULE,
  PROM_SCHEDULES_BY_PATHWAY,
  type PromScheduleSet,
  type PromScheduleStep,
} from './prom-schedules';
import { PROM_DEFINITION_BY_ID, type PromId } from './prom-definitions';

export interface AdminSchedulePanelProps {
  /**
   * Optional callback invoked when the user clicks an instrument chip.
   * The PROMs view wires this to the InstrumentDetailPanel selection.
   */
  onSelectInstrument?: (id: PromId) => void;
  /**
   * Optional class hook for layout-specific overrides.
   */
  className?: string;
}

const STEP_BADGE_TONE: Record<
  PromScheduleStep['status'],
  { bgClass: string; fgClass: string; symbol: string }
> = {
  complete: {
    bgClass: 'bg-status-active-bg',
    fgClass: 'text-status-active-fg',
    symbol: '✓', // U+2713 CHECK MARK
  },
  scheduled: {
    bgClass: 'bg-surface-muted',
    fgClass: 'text-text-secondary',
    symbol: '',
  },
};

function StepRow({
  step,
  scheduleId,
  onSelectInstrument,
}: {
  step: PromScheduleStep;
  scheduleId: string;
  onSelectInstrument?: (id: PromId) => void;
}) {
  const tone = STEP_BADGE_TONE[step.status];
  return (
    <li
      role="listitem"
      data-prom-schedule-id={scheduleId}
      data-prom-step-status={step.status}
      data-prom-step-ordinal={step.ordinal}
      className="flex flex-col gap-gutter-sm rounded-signal-md bg-surface-base p-gutter-md shadow-signal-card ring-1 ring-inset ring-surface-border md:flex-row md:items-center md:justify-between"
    >
      <div className="flex items-center gap-gutter-md">
        <span
          aria-hidden="true"
          className="flex h-8 w-8 items-center justify-center rounded-signal-pill bg-surface-muted text-signal-sm font-semibold text-text-primary"
        >
          {step.ordinal}
        </span>
        <div className="flex flex-col">
          <span className="text-signal-base font-semibold text-text-primary">
            Step {step.ordinal}
          </span>
          <span className="text-signal-xs text-text-tertiary">
            {step.dateLabel}
          </span>
        </div>
        <span
          data-prom-step-badge={step.status}
          className={[
            'rounded-signal-pill px-gutter-sm py-1 text-signal-xs font-semibold uppercase tracking-wide',
            tone.bgClass,
            tone.fgClass,
          ].join(' ')}
        >
          {tone.symbol ? (
            <span aria-hidden="true" className="mr-1">
              {tone.symbol}
            </span>
          ) : null}
          {step.statusLabel}
        </span>
      </div>
      <ul
        role="list"
        aria-label={`Step ${step.ordinal} instruments`}
        className="flex flex-wrap items-center gap-gutter-sm"
      >
        {step.instruments.map((instrumentId) => {
          const def = PROM_DEFINITION_BY_ID[instrumentId];
          if (!def) return null;
          return (
            <li
              key={instrumentId}
              role="listitem"
              data-prom-instrument-chip={instrumentId}
            >
              <button
                type="button"
                onClick={() => onSelectInstrument?.(instrumentId)}
                data-testid={`admin-schedule-chip-${instrumentId}-step-${step.ordinal}`}
                className="rounded-signal-pill bg-surface-muted px-gutter-sm py-1 text-signal-xs font-medium text-text-secondary ring-1 ring-inset ring-surface-border hover:bg-signal-primary-100 hover:text-signal-primary-700"
              >
                {def.title}
              </button>
            </li>
          );
        })}
      </ul>
    </li>
  );
}

function ScheduleBlock({
  schedule,
  onSelectInstrument,
}: {
  schedule: PromScheduleSet;
  onSelectInstrument?: (id: PromId) => void;
}) {
  return (
    <div
      data-testid={`admin-schedule-block-${schedule.pathwayDisplay
        .toLowerCase()
        .replace(/\s+/g, '-')}`}
      className="flex flex-col gap-gutter-sm"
    >
      <h4 className="text-signal-base font-semibold text-text-primary">
        {schedule.pathwayDisplay} Schedule
      </h4>
      <ul role="list" className="flex flex-col gap-gutter-sm">
        {schedule.steps.map((step) => (
          <StepRow
            key={step.id}
            step={step}
            scheduleId={schedule.pathwayDisplay}
            onSelectInstrument={onSelectInstrument}
          />
        ))}
      </ul>
    </div>
  );
}

export function AdminSchedulePanel({
  onSelectInstrument,
  className,
}: AdminSchedulePanelProps) {
  const activePatientId = usePatientStore((s) => s.activePatientId);
  const patient = activePatientId ? getPatientById(activePatientId) : undefined;

  // Resolve the schedule from the active patient's pathway. Fall back to
  // the Cardiometabolic Pathway when the patient is missing or carries
  // an unknown pathway value.
  const activeSchedule: PromScheduleSet = patient
    ? PROM_SCHEDULES_BY_PATHWAY[patient.pathway] ??
      PROM_SCHEDULES_BY_PATHWAY['cardiometabolic-pathway']
    : PROM_SCHEDULES_BY_PATHWAY['cardiometabolic-pathway'];

  const [longevityProfileEnabled, setLongevityProfileEnabled] = useState(false);

  return (
    <section
      aria-labelledby="admin-schedule-title"
      data-testid="admin-schedule-panel"
      data-prom-pathway={activeSchedule.pathway}
      className={[
        'flex flex-col gap-gutter-md rounded-signal-lg border border-surface-border bg-surface-base p-gutter-lg shadow-signal-card',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="flex flex-wrap items-center justify-between gap-gutter-sm">
        <div className="flex flex-col gap-1">
          <p className="text-signal-xs uppercase tracking-wide text-text-tertiary">
            Admin Schedule
          </p>
          <h3
            id="admin-schedule-title"
            className="text-signal-xl font-semibold text-text-primary"
          >
            {activeSchedule.pathwayDisplay}
          </h3>
        </div>
        <button
          type="button"
          aria-pressed={longevityProfileEnabled}
          data-testid="admin-schedule-longevity-toggle"
          onClick={() => setLongevityProfileEnabled((v) => !v)}
          className={[
            'rounded-signal-md border px-gutter-md py-1 text-signal-sm font-medium transition-colors',
            longevityProfileEnabled
              ? 'border-signal-primary-600 bg-signal-primary-100 text-signal-primary-700'
              : 'border-surface-border bg-surface-base text-text-secondary hover:bg-surface-muted',
          ].join(' ')}
        >
          {longevityProfileEnabled
            ? 'Longevity Profile on'
            : 'Add Longevity Profile'}
        </button>
      </header>

      <ScheduleBlock
        schedule={activeSchedule}
        onSelectInstrument={onSelectInstrument}
      />

      {longevityProfileEnabled ? (
        <div
          data-testid="admin-schedule-longevity-block"
          className="flex flex-col gap-gutter-sm border-t border-surface-border pt-gutter-md"
        >
          <ScheduleBlock
            schedule={LONGEVITY_PROFILE_SCHEDULE}
            onSelectInstrument={onSelectInstrument}
          />
          <p className="text-signal-xs text-text-tertiary">
            Longevity Profile is a cross-pathway overlay. There is no
            FHIR PlanDefinition binding yet; submissions still emit the
            QuestionnaireResponse + Observation pair plus an AuditEvent
            with source proms-tab.
          </p>
        </div>
      ) : null}
    </section>
  );
}
